import { DragDropModule } from '@angular/cdk/drag-drop';
import { PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NasModelModule } from '@sama/ng-app-state';
import { SamaScrollSectionNavigationModule } from '@sama/ui-library';
import { ImageContentModule } from '../../../2d/image-content/image-content.module';
import { VideoContentModule } from '../../../2d/video-content/video-content.module';
import { PointCloudContentModule } from '../../../3d/point-cloud-content.module';
import { TopFiveClassesComponent } from '../../../core/class-selector/top-five-classes/top-five-classes.component';
import { SplitViewOutputLabelComponent } from '../../../core/h3ml/dsl/sensor-fusion/split-view-output-label/split-view-output-label.component';
import { CVToolbarComponent } from '../../../core/h3ml/dsl/sensor-fusion/split-view-toolbar/cv-toolbar.component';
import { CvHelpModalButtonComponent } from '../../../core/h3ml/dsl/sensor-fusion/split-view-toolbar/help-modal-button/cv-help-modal-button.component';
import { SplitViewComponent } from '../../../core/h3ml/dsl/sensor-fusion/split-view/split-view.component';
import { SettingsComponent } from '../../../core/h3ml/dsl/sensor-fusion/view-settings/settings/settings.component';
import { AnnotationSharedModule as CoreAnnotationSharedModule } from '../../../core/outputs/annotation/annotation-shared/annotation-shared.module';
import { LayersComponent } from '../../../core/outputs/annotation/annotation-shared/layers/layers.component';
import { FloatingFeedbackPanelComponent } from '../../../core/outputs/annotation/annotation-shared/toolbars/floating-feedback-panel/floating-feedback-panel.component';
import { EditTagsButtonComponent } from '../../../core/outputs/annotation/annotation-shared/toolbars/shape-list-and-tag-editor/shape-list/edit-tags-button/edit-tags-button.component';
import { ToggleShapeVisibilityButtonComponent } from '../../../core/outputs/annotation/annotation-shared/toolbars/shape-list-and-tag-editor/shape-list/toggle-shape-visibility-button/toggle-shape-visibility-button.component';
import { BulkRemoveKeyframesModalComponent } from '../../../core/outputs/annotation/annotation-shared/toolbars/shape-list/bulk-remove-keyframes-modal/bulk-remove-keyframes-modal.component';
import { SplitViewHelpModalComponent } from '../../../core/outputs/annotation/annotation-shared/toolbars/static-toolbar/help-modal/split-view-help-modal.component';
import { ShowFeedbackButtonComponent } from '../../../core/outputs/annotation/annotation-shared/toolbars/static-toolbar/show-feedback-button/show-feedback-button.component';
import { ZoomPanelCursorComponent } from '../../../core/outputs/annotation/annotation-shared/zoom-panel/zoom-panel-cursor.component';
import { ZoomPanelComponent } from '../../../core/outputs/annotation/annotation-shared/zoom-panel/zoom-panel.component';
import { ScrollableListComponent } from '../../../core/shared/scrollable-list/scrollable-list.component';
import { OutputTabsComponent } from '../../../core/split-view/output-tabs/output-tabs.component';
import { AttributeDisplayLabelComponent } from '../../../shared/forms/basic-inputs/attribute-input/attribute-display-label/attribute-display-label.component';
import { BooleanAttributeInputComponent } from '../../../shared/forms/basic-inputs/attribute-input/boolean-attribute-input/boolean-attribute-input.component';
import { ChoiceAttributeInputComponent } from '../../../shared/forms/basic-inputs/attribute-input/choice-attribute-input/choice-attribute-input.component';
import { TextAttributeInputComponent } from '../../../shared/forms/basic-inputs/attribute-input/text-attribute-input/text-attribute-input.component';
import { SharedModule as UrsaShared } from '../../../shared/shared.module';
import { NestableOutputsModule } from '../../nestable/nestable-outputs.module';
import { AddRandomShapesComponent } from './layers/add-random-shapes/add-random-shapes.component';
import { AnnotationPanelComponent } from './side-panel/annotation-panel/annotation-panel.component';
import { GroundingSettingsComponent } from './side-panel/grounding-settings/grounding-settings.component';
import { LayoutSettingsComponent } from './side-panel/layout-settings/layout-settings.component';
import { PersistCheckboxComponent } from './side-panel/persist-checkbox/persist-checkbox.component';
import { PointColorSettingsComponent } from './side-panel/point-color-settings/point-color-settings.component';
import { RegionOfInterestSettingsComponent } from './side-panel/region-of-interest-settings/region-of-interest-settings.component';
import { SensorColorOptionComponent } from './side-panel/sensor-color-option/sensor-color-option.component';
import { SettingsHeaderComponent } from './side-panel/settings-header/settings-header.component';
import { SettingsPanelComponent } from './side-panel/settings-panel/settings-panel.component';
import { SidePanelTabsComponent } from './side-panel/side-panel-tabs/side-panel-tabs.component';
import { SidePanelComponent } from './side-panel/side-panel.component';
import { ViewSettingsOpacitySliderComponent } from './side-panel/view-settings-opacity-slider/view-settings-opacity-slider.component';
import { ViewSettingsRangeSliderComponent } from './side-panel/view-settings-range-slider/view-settings-range-slider.component';
import { ViewSettingsSliderComponent } from './side-panel/view-settings-slider/view-settings-slider.component';
import { ViewSettings2dComponent } from './side-panel/view-settings2d/view-settings2d.component';
import { ViewSettings3dComponent } from './side-panel/view-settings3d/view-settings3d.component';
import { AnnotationEditPaneComponent } from './toolbars/shape-list-and-tag-editor/annotation-list/annotation-edit-pane/annotation-edit-pane.component';
import { AnnotationLabelComponent } from './toolbars/shape-list-and-tag-editor/annotation-list/annotation-label/annotation-label.component';
import { AnnotationListSectionHeaderComponent } from './toolbars/shape-list-and-tag-editor/annotation-list/annotation-list-section-header/annotation-list-section-header.component';
import { AnnotationListSectionComponent } from './toolbars/shape-list-and-tag-editor/annotation-list/annotation-list-section/annotation-list-section.component';
import { AnnotationListComponent } from './toolbars/shape-list-and-tag-editor/annotation-list/annotation-list.component';
import { ClassSelectorComponent } from './toolbars/shape-list-and-tag-editor/annotation-list/class-selector/class-selector.component';
import { TagPaneComponent } from './toolbars/shape-list-and-tag-editor/annotation-list/tags-pane/tag-pane.component';
import { UnscopeObjectButtonComponent } from './toolbars/shape-list-and-tag-editor/annotation-list/unscope-object-button/unscope-object-button.component';
import { ObjectFeedbackIndicatorComponent } from './toolbars/shape-list-and-tag-editor/object-list/object-feedback-indicator/object-feedback-indicator.component';
import { ObjectListItemDropAreasComponent } from './toolbars/shape-list-and-tag-editor/object-list/object-list-item-drop-areas/object-list-item-drop-areas.component';
import { ObjectListItemComponent } from './toolbars/shape-list-and-tag-editor/object-list/object-list-item/object-list-item.component';
import { ObjectListComponent } from './toolbars/shape-list-and-tag-editor/object-list/object-list.component';
import { DeleteShapesButtonComponent } from './toolbars/shape-list-and-tag-editor/objectless-shape-list/delete-shapes-button/delete-shapes-button.component';
import { GroupShapesButtonComponent } from './toolbars/shape-list-and-tag-editor/objectless-shape-list/group-shapes-button/group-shapes-button.component';
import { KeyLocationActionsComponent } from './toolbars/shape-list-and-tag-editor/objectless-shape-list/key-location-actions/key-location-actions.component';
import { ObjectlessShapeListComponent } from './toolbars/shape-list-and-tag-editor/objectless-shape-list/objectless-shape-list/objectless-shape-list.component';
import { SelectAllButtonComponent } from './toolbars/shape-list-and-tag-editor/objectless-shape-list/select-all-button/select-all-button.component';
import { ShapeGroupComponent } from './toolbars/shape-list-and-tag-editor/objectless-shape-list/shape-group/shape-group.component';
import { ShapeListShapeComponent } from './toolbars/shape-list-and-tag-editor/objectless-shape-list/shape-list-shape/shape-list-shape.component';
import { ShapeFilterComponent } from './toolbars/shape-list-and-tag-editor/shape-filter/shape-filter.component';
import { ShapeListAndTagEditorComponent } from './toolbars/shape-list-and-tag-editor/shape-list-and-tag-editor.component';
import { ShapeFeedbackIndicatorComponent } from './toolbars/shape-list-and-tag-editor/shape-list/shape-feedback-indicator/shape-feedback-indicator.component';
import { ShapeListItemComponent } from './toolbars/shape-list-and-tag-editor/shape-list/shape-list-item/shape-list-item.component';
import { ShapeListComponent } from './toolbars/shape-list-and-tag-editor/shape-list/shape-list.component';
import { ShapeSubpointCountComponent } from './toolbars/shape-list-and-tag-editor/shape-list/shape-subpoint-count/shape-subpoint-count.component';
import { ObjectlessNestedOutputPanelComponent } from './toolbars/shape-list-and-tag-editor/tag-editor/nested-output-panel/objectless-nested-output-panel.component';
import { ObjectlessTagEditorComponent } from './toolbars/shape-list-and-tag-editor/tag-editor/objectless-tag-editor.component';

const exportedItems = [
  AddRandomShapesComponent,
  EditTagsButtonComponent,
  FloatingFeedbackPanelComponent,
  LayersComponent,
  SettingsComponent,
  SidePanelComponent,
  ZoomPanelComponent,
  ShapeListAndTagEditorComponent,
  ToggleShapeVisibilityButtonComponent,
  BulkRemoveKeyframesModalComponent,
  CVToolbarComponent,
  SplitViewComponent,
  CvHelpModalButtonComponent,
  SplitViewHelpModalComponent,
  SplitViewOutputLabelComponent,
];

@NgModule({
  imports: [
    CommonModule,
    CoreAnnotationSharedModule,
    DragDropModule,
    FontAwesomeModule,
    FormsModule,
    ImageContentModule,
    MatCardModule,
    MatFormFieldModule,
    MatMenuModule,
    MatSelectModule,
    MatTooltipModule,
    NasModelModule,
    NestableOutputsModule,
    OutputTabsComponent,
    PointCloudContentModule,
    ReactiveFormsModule,
    ScrollingModule,
    UrsaShared,
    VideoContentModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    MatTabsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRippleModule,
    MatSlideToggleModule,
    MatTreeModule,
    MatBadgeModule,
    MatButtonToggleModule,
    MatSliderModule,
    PortalModule,
    MatDivider,
    SamaScrollSectionNavigationModule,
    SettingsHeaderComponent,
    ScrollableListComponent,
    RegionOfInterestSettingsComponent,
    TopFiveClassesComponent,
    ShowFeedbackButtonComponent,
  ],
  declarations: [
    exportedItems,
    AnnotationEditPaneComponent,
    AnnotationLabelComponent,
    ClassSelectorComponent,
    DeleteShapesButtonComponent,
    GroupShapesButtonComponent,
    KeyLocationActionsComponent,
    ObjectlessNestedOutputPanelComponent,
    SelectAllButtonComponent,
    ShapeFilterComponent,
    ObjectlessTagEditorComponent,
    ShapeListShapeComponent,
    ShapeGroupComponent,
    ObjectlessShapeListComponent,
    ToggleShapeVisibilityButtonComponent,
    ZoomPanelCursorComponent,
    ObjectListComponent,
    ObjectListItemComponent,
    ShapeListComponent,
    ShapeListItemComponent,
    ShapeFeedbackIndicatorComponent,
    ShapeSubpointCountComponent,
    AnnotationListComponent,
    SidePanelComponent,
    AnnotationListSectionComponent,
    AnnotationListSectionHeaderComponent,
    UnscopeObjectButtonComponent,
    TagPaneComponent,
    ObjectListItemDropAreasComponent,
    BooleanAttributeInputComponent,
    ChoiceAttributeInputComponent,
    TextAttributeInputComponent,
    AttributeDisplayLabelComponent,
    AnnotationPanelComponent,
    LayoutSettingsComponent,
    SettingsPanelComponent,
    SidePanelTabsComponent,
    ViewSettings3dComponent,
    ViewSettingsSliderComponent,
    ViewSettingsOpacitySliderComponent,
    ViewSettings2dComponent,
    GroundingSettingsComponent,
    PointColorSettingsComponent,
    ViewSettingsRangeSliderComponent,
    SensorColorOptionComponent,
    PersistCheckboxComponent,
    ObjectFeedbackIndicatorComponent,
  ],
  exports: [
    exportedItems,
    CoreAnnotationSharedModule,
    ShowFeedbackButtonComponent,
  ],
})
export class AnnotationSharedModule {}
